import { ArrowBack, ListAlt, OpenInNew } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from 'services/api';
import {
  ListaDetalheResumo,
  TransacaoLista,
} from '../AdminListaPresentesPage/AdminListaPresentesPage.types';

const formatCurrency = (valor: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor);

const truncate = (value: string | null | undefined, length = 70) => {
  if (!value) return '';
  if (value.length <= length) return value;
  return `${value.slice(0, length - 1)}…`;
};

const AdminListaRecebidosPage: React.FC = () => {
  const { listaId } = useParams<{ listaId: string }>();
  const navigate = useNavigate();

  const [lista, setLista] = useState<ListaDetalheResumo | null>(null);
  const [transacoes, setTransacoes] = useState<TransacaoLista[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const encerrada = lista?.ativa === false || !!lista?.encerradaEm;

  const totais = useMemo(() => {
    let totalRecebido = 0;
    let totalPendente = 0;

    transacoes.forEach((t) => {
      const valor = Number(t.valor) || 0;
      if (t.status === 'pago' || t.status === 'confirmado') {
        totalRecebido += valor;
      } else {
        totalPendente += valor;
      }
    });

    return { totalRecebido, totalPendente };
  }, [transacoes]);

  const recarregar = useCallback(async () => {
    if (!listaId) {
      setError('Lista não informada.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const [listaRes, transacoesRes] = await Promise.all([
        api.get<{ lista: ListaDetalheResumo }>(`/listas/${listaId}`),
        api.get<{ transacoes: TransacaoLista[] }>(`/listas/${listaId}/transacoes`),
      ]);

      setLista(listaRes.data.lista);
      setTransacoes(
        (transacoesRes.data.transacoes ?? []).map((t) => ({
          ...t,
          valor: Number(t.valor),
          present: {
            ...t.present,
            valor: Number(t.present.valor),
          },
        }))
      );
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao carregar presentes recebidos.';
      setError(message);
    } finally {
      setLoading(false);
      setActionLoadingId(null);
    }
  }, [listaId]);

  const tituloLista = useMemo(() => {
    if (!lista) return '';
    return lista.tituloLista || lista.template?.titulo || 'Lista de presentes';
  }, [lista]);

  useEffect(() => {
    void recarregar();
  }, [listaId, recarregar]);

  const atualizarStatus = useCallback(
    async (transacaoId: string, status: 'confirmado' | 'pendente') => {
      if (!listaId) return;
      try {
        setActionLoadingId(transacaoId);
        await api.put(`/listas/${listaId}/transacoes/${transacaoId}/confirmar`, { status });
        setTransacoes((prev) =>
          prev.map((tx) =>
            tx.id === transacaoId
              ? {
                  ...tx,
                  status,
                }
              : tx
          )
        );
      } catch (err: any) {
        const message = err?.response?.data?.error || 'Erro ao atualizar status.';
        setError(message);
      } finally {
        setActionLoadingId(null);
      }
    },
    [listaId]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth={960} mx="auto" p={3}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
              Voltar
            </Button>
          </Stack>
          <Alert severity="error">{error}</Alert>
        </Stack>
      </Box>
    );
  }

  return (
    <Box maxWidth={1100} mx="auto" p={{ xs: 2, sm: 3, md: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Presentes recebidos / PIX gerados
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Veja quem presenteou, status e abra o QR/linha digitável quando necessário.
          </Typography>
          {tituloLista && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mt={1}>
              <Typography variant="body2" color="text.secondary">
                Lista: <strong>{tituloLista}</strong>
              </Typography>
              {encerrada && <Chip label="Encerrada" size="small" color="error" />}
            </Stack>
          )}
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
          <Button startIcon={<ListAlt />} variant="outlined" onClick={() => navigate(-1)}>
            Voltar
          </Button>
          {lista && (
            <Button
              startIcon={<OpenInNew />}
              variant="outlined"
              onClick={() => !encerrada && navigate(`/lista/${lista.slug}`)}
              disabled={encerrada}
            >
              Voltar para lista
            </Button>
          )}
        </Stack>
      </Stack>

      {transacoes.length === 0 ? (
        <Alert severity="info">Nenhum presente recebido ainda.</Alert>
      ) : (
        <>
          <Stack spacing={1.5} mb={2} direction={{ xs: 'column', sm: 'row' }}>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Total recebido
              </Typography>
              <Typography variant="h6" color="success.main" fontWeight={700}>
                {formatCurrency(totais.totalRecebido)}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Total pendente
              </Typography>
              <Typography variant="h6" color="warning.main" fontWeight={700}>
                {formatCurrency(totais.totalPendente)}
              </Typography>
            </Paper>
          </Stack>
          {encerrada && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Lista encerrada: alteração de status desativada.
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Presente</TableCell>
                  <TableCell>Presenteador</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transacoes.map((t) => {
                  const isPago = t.status === 'pago' || t.status === 'confirmado';
                  const mensagemCurta = truncate(t.mensagem, 60) || 'Sem mensagem';
                  const nomeCurto = truncate(t.present.nome, 48);
                  const codigoInuco = (t.txId || t.id).slice(0, 8).toUpperCase();
                  const mensagemComCodigo = `${codigoInuco} · ${mensagemCurta}`;
                  const statusClickable = !encerrada && actionLoadingId !== t.id;
                  const proximoStatus: 'confirmado' | 'pendente' = isPago
                    ? 'pendente'
                    : 'confirmado';

                  return (
                    <TableRow key={t.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600}>
                            {new Date(t.createdAt).toLocaleDateString('pt-BR')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(t.createdAt).toLocaleTimeString('pt-BR')}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Tooltip
                            title={t.present.nome}
                            disableHoverListener={t.present.nome.length <= 48}
                          >
                            <Typography variant="body2" noWrap fontWeight={600}>
                              {nomeCurto}
                            </Typography>
                          </Tooltip>
                          <Tooltip
                            title={t.mensagem ? `${codigoInuco} · ${t.mensagem}` : codigoInuco}
                            disableHoverListener={!t.mensagem || t.mensagem.length <= 60}
                          >
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {mensagemComCodigo}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {t.presenteadorNome || 'Anônimo'}
                          </Typography>
                          {t.presenteadorEmail && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {t.presenteadorEmail}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          {formatCurrency(t.valor)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isPago ? 'Recebido' : 'Pendente'}
                          color={isPago ? 'success' : 'warning'}
                          size="small"
                          variant={isPago ? 'filled' : 'outlined'}
                          onClick={
                            statusClickable
                              ? () => void atualizarStatus(t.id, proximoStatus)
                              : undefined
                          }
                          clickable={statusClickable}
                          sx={{ cursor: statusClickable ? 'pointer' : 'default' }}
                          disabled={!statusClickable}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AdminListaRecebidosPage;
